import React from "react";
import { Formik, FormikProps } from "formik";
import { Grid, MenuItem } from "@material-ui/core";
import * as Yup from "yup";
import renderCommonFormField from "@src/Components/Common/FormFields/renderCommonFormField";
import FormSwitch from "@src/Components/Common/FormFields/FormSwitch/FormSwitch";
import { observer, inject } from "mobx-react";
import { IValuesStore } from "@src/Stores/Values/types";
import { STFormTitleTypography } from "@src/Components/Ui/STFormTitleTypohraphy";
import _ from "lodash";
import { IManagementStore } from "@src/Stores/Management/types";
import { computed, observable } from "mobx";
import {
  STFormWrapper,
  STStateGrid,
  STTextField,
  STAddButtonGrid,
  STItemGrid,
  LocationFormSimpleSelect,
  STTypeSelectGrid,
  STInputLabelType,
  STSelect,
} from "./styles";
import { ButtonsForm } from "@src/Components/Common/ButtonsForm/ButtonsForm";
import { IAuthApplication } from "@src/Interfaces/Entity/OAuthApplication";
import { Values } from "@src/Tools/Values";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { STFabGreenAdd, STFabTooltip } from "@src/Components/Ui/STFab";
import { STContentWrapper } from "@src/Components/Common/SimpleModal/styles";
import { IAuthStore } from "@src/Stores/Auth/types";

const oAuthApplicationSchema = Yup.object().shape({
  name: Yup.string().required(() => {
    return Values.getLocalizationValue("fieldRequired");
  }),
  redirectUrls: Yup.array()
    .required(() => {
      return Values.getLocalizationValue("fieldRequired");
    })
    .nullable(),
});

interface IProps {
  title: string;
  initialValues: IAuthApplication;
  onSubmit: (values: IAuthApplication) => void;
  onClose: () => void;
  values?: IValuesStore;
  management?: IManagementStore;
  auth?: IAuthStore;
}

@inject("auth", "values", "management")
@observer
class AuthApplicationForm extends React.Component<IProps> {
  @observable redirectUrlInput: string = "";

  @computed get isEditMode(): boolean {
    return this.props.initialValues.name !== "";
  }

  @computed get hasLocation(): boolean {
    return this.props.initialValues.locationId !== "00000000-0000-0000-0000-000000000000";
  }

  handleRedirectUrlChange = (value: string) => {
    this.redirectUrlInput = value;
    this.forceUpdate();
  };

  handleProtocolTypeChange = (event: any, formikProps: any) => {
    const newType = parseInt(event.target.value);
    formikProps.setFieldValue("protocolType", newType);
  };

  render() {
    return (
      <Formik
        enableReinitialize
        initialValues={this.props.initialValues}
        validationSchema={oAuthApplicationSchema}
        onSubmit={this.props.onSubmit}
        render={(formikProps: FormikProps<IAuthApplication>) => (
          <STFormWrapper onSubmit={formikProps.handleSubmit}>
            <STFormTitleTypography>{this.props.title}</STFormTitleTypography>
            <STContentWrapper>
                <STTypeSelectGrid>
                    <STInputLabelType>{Values.getLocalizationValue("protocolType")}</STInputLabelType>
                    <STSelect
                        value={formikProps.values.protocolType}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleProtocolTypeChange(event, formikProps)}
                        disabled={this.isEditMode}
                        error={!!formikProps.errors.protocolType}
                    >
                        {Object.entries(this.props.values.authProtocolTypes || {})
                            .sort()
                            .map((key) => (
                                <MenuItem key={key[1]} value={key[1]}>
                                    {key[0]}
                                </MenuItem>
                            ))}
                    </STSelect>
                </STTypeSelectGrid>
              {renderCommonFormField<IAuthApplication>("name", Values.getLocalizationValue("name"), formikProps)}
              {this.props.auth.user.location.primary && !this.props.values.location && (
                <Grid container justify="flex-start" alignItems="center">
                  <Grid item xs={7}>
                    <LocationFormSimpleSelect<IAuthApplication, Location>
                      fieldName="locationId"
                      items={this.props.management.activeLocations}
                      keyAsTitle="name"
                      keyAsValue="id"
                      formikProps={formikProps}
                      label={Values.getLocalizationValue("location")}
                      disabled={this.isEditMode && this.hasLocation}
                    />
                  </Grid>
                </Grid>
              )}
              {this.isEditMode &&
                renderCommonFormField<IAuthApplication>(
                  "clientId",
                  Values.getLocalizationValue("clientId"),
                  formikProps,
                  false,
                  null,
                  true
                )}
              {this.isEditMode &&
                renderCommonFormField<IAuthApplication>(
                  "clientSecret",
                  Values.getLocalizationValue("clientSecret"),
                  formikProps,
                  false,
                  null,
                  true
                )}
              <Grid container>
                <Grid item xs={10}>
                  <STTextField
                    label={Values.getLocalizationValue("redirectUrl")}
                    value={this.redirectUrlInput}
                    onChange={(event: any) => this.handleRedirectUrlChange(event.target.value)}
                    error={!!formikProps.errors.redirectUrls}
                    helperText={formikProps.errors.redirectUrls}
                  />
                </Grid>
                <STAddButtonGrid item xs={2}>
                  <STFabGreenAdd
                    size="small"
                    onClick={() => {
                      if (
                        !this.redirectUrlInput ||
                        formikProps.values.redirectUrls.find((url) => url === this.redirectUrlInput)
                      ) {
                        return;
                      }
                      formikProps.setFieldValue("redirectUrls", [
                        ...formikProps.values.redirectUrls,
                        this.redirectUrlInput,
                      ]);
                      this.handleRedirectUrlChange("");
                    }}
                    aria-label="Add"
                  >
                    <AddIcon />
                  </STFabGreenAdd>
                </STAddButtonGrid>
              </Grid>
              <Grid container justify="flex-end">
                {formikProps.values.redirectUrls.map((redirectUrl, index) => (
                  <Grid item xs={11} key={index}>
                    <Grid container>
                      <STItemGrid item xs={10}>
                        <span>{`${index + 1}. ${redirectUrl}`}</span>
                      </STItemGrid>
                      <Grid item xs={2}>
                        <STFabTooltip
                          tooltipTitle={Values.getLocalizationValue("remove")}
                          onClick={() =>
                            formikProps.setFieldValue(
                              "redirectUrls",
                              formikProps.values.redirectUrls.filter((url) => url !== redirectUrl)
                            )
                          }
                          size="small"
                          color="secondary"
                        >
                          <DeleteIcon />
                        </STFabTooltip>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
                <STStateGrid container alignItems="center" spacing={16}>
                <Grid item>{Values.getLocalizationValue("active")}</Grid>
                <Grid item>
                  <FormSwitch<IAuthApplication>
                    trueAs={this.props.values.states.Active}
                    falseAs={this.props.values.states.Deactivated}
                    fieldName="state"
                    formikProps={formikProps}
                  />
                </Grid>
              </STStateGrid>
            </STContentWrapper>
            <ButtonsForm onSubmit={formikProps.handleSubmit} onClose={this.props.onClose} />
          </STFormWrapper>
        )}
      />
    );
  }
}

export default AuthApplicationForm;
